import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createPayout,
  getMentorPayments,
  MentorOpenBalance,
  MentorPaymentItem,
  MentorPayoutsKeys,
} from "@/features/payouts/api/mentor-payouts";
import ConfirmPayoutDialog from "@/features/payouts/components/ConfirmPayoutDialog";

interface MentorPaymentsDialogProps {
  mentor: MentorOpenBalance | null;
  onClose: () => void;
}

const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getPaymentDateRange = (payments: MentorPaymentItem[]) => {
  if (payments.length === 0) return null;

  const dates = payments.map((p) => new Date(p.paidAt).getTime());
  const oldestDate = new Date(Math.min(...dates));
  const newestDate = new Date(Math.max(...dates));

  return {
    startDate: oldestDate.toISOString(),
    endDate: newestDate.toISOString(),
  };
};

const MentorPaymentsDialog = ({
  mentor,
  onClose,
}: MentorPaymentsDialogProps) => {
  const queryClient = useQueryClient();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { data: payments = [], isLoading } = useQuery({
    queryKey: [MentorPayoutsKeys.GET_MENTOR_PAYMENTS, mentor?.mentorId],
    queryFn: () => getMentorPayments(mentor!.mentorId),
    enabled: !!mentor?.mentorId,
  });

  const createPayoutMutation = useMutation({
    mutationFn: createPayout,
    onSuccess: (_data, variables) => {
      toast.success("Payout created successfully");
      queryClient.invalidateQueries({
        queryKey: [MentorPayoutsKeys.GET_OPEN_BALANCES],
      });
      queryClient.invalidateQueries({
        queryKey: [MentorPayoutsKeys.GET_MENTOR_PAYMENTS, variables.mentorId],
      });
      queryClient.invalidateQueries({
        queryKey: [MentorPayoutsKeys.GET_PAYOUT_HISTORY],
      });
      setShowConfirmDialog(false);
      onClose();
    },
    onError: () => {
      toast.error("Failed to create payout");
    },
  });

  const handleCreatePayoutClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmPayout = () => {
    if (!mentor) return;

    const dateRange = getPaymentDateRange(payments);
    if (!dateRange) return;

    createPayoutMutation.mutate({
      mentorId: mentor.mentorId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      expectedAmountUsd: mentor.openBalanceUsd,
    });
  };

  return (
    <Dialog open={!!mentor} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Payments for {mentor?.mentorName}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No available payments found
          </div>
        ) : (
          <>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Paid At</TableHead>
                    <TableHead className="text-right">Gross Amount</TableHead>
                    <TableHead className="text-right">Mentor Fees</TableHead>
                    <TableHead className="text-right">Fees (USD)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.paymentId}>
                      <TableCell className="font-medium">
                        {payment.customerEmail || "-"}
                      </TableCell>
                      <TableCell>
                        {format(new Date(payment.paidAt), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(payment.grossAmount, payment.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(payment.mentorFees, payment.currency)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(payment.mentorFeesUsd, "USD")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>

            <DialogFooter>
              <Button onClick={handleCreatePayoutClick}>Create Payout</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>

      {mentor && (
        <ConfirmPayoutDialog
          open={showConfirmDialog}
          onOpenChange={setShowConfirmDialog}
          mentorName={mentor.mentorName}
          amount={formatCurrency(mentor.openBalanceUsd, "USD")}
          onConfirm={handleConfirmPayout}
          isPending={createPayoutMutation.isPending}
        />
      )}
    </Dialog>
  );
};

export default MentorPaymentsDialog;
